import React from "react";
import { CollatedActivity } from "../../../types/collatedUpdates";
import { relativeTime, absoluteTime } from "./relativeTime";
import { Tooltip } from "@vibe/core";
import Avatar from "./Avatar";

// Emphasis for a new/current value: darker + semibold so the eye lands on it.
const Val = ({ children }: { children: React.ReactNode }) => (
  <span className="font-semibold text-[#323338]">{children}</span>
);
// A prior value being replaced: muted + struck-through, so "from X to Y" reads
// at a glance without the old value competing with the new one.
const Prev = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[#9699A6] line-through">{children}</span>
);

// Human-readable one-liner for an activity entry, switched on activityType.
// Returns formatted JSX (bold new values, muted/struck prior values) so the
// meaningful parts stand out. Missing/empty optional fields degrade gracefully
// (never render "undefined").
// `isFirstCreate` distinguishes the item's original creation from the copies a
// sync later spins up on other boards: the first create reads as an authored
// action ("created this item in X"), the rest as the item being mirrored in
// ("This item was added to Y"). See ActivityLog.
const activitySentence = (entry: CollatedActivity, isFirstCreate = true): React.ReactNode => {
  const { activityType, columnTitle, fromLabel, toLabel, extra, boardName } = entry;

  switch (activityType) {
    case "column_value": {
      const column = <Val>{columnTitle || "a column"}</Val>;
      if (fromLabel && toLabel)
        return (
          <>
            changed {column} from <Prev>{fromLabel}</Prev> to <Val>{toLabel}</Val>
          </>
        );
      if (toLabel)
        return (
          <>
            set {column} to <Val>{toLabel}</Val>
          </>
        );
      return <>changed {column}</>;
    }
    case "create":
      if (!isFirstCreate) {
        return boardName ? (
          <>
            This item was added to <Val>{boardName}</Val>
          </>
        ) : (
          "This item was added"
        );
      }
      return boardName ? (
        <>
          created this item in <Val>{boardName}</Val>
        </>
      ) : (
        "created this item"
      );
    case "rename":
      if (fromLabel && toLabel)
        return (
          <>
            renamed from <Prev>{fromLabel}</Prev> to <Val>{toLabel}</Val>
          </>
        );
      if (toLabel)
        return (
          <>
            renamed to <Val>{toLabel}</Val>
          </>
        );
      return "renamed this item";
    case "move_group":
      return extra?.group ? (
        <>
          moved to group <Val>{extra.group}</Val>
        </>
      ) : (
        "moved to a different group"
      );
    case "move_board":
      return extra?.board ? (
        <>
          moved to board <Val>{extra.board}</Val>
        </>
      ) : (
        "moved to a different board"
      );
    case "create_subitem":
      return extra?.name ? (
        <>
          added subitem <Val>{extra.name}</Val>
        </>
      ) : (
        "added a subitem"
      );
    default:
      return "updated this item";
  }
};

// Activity events are shown as the author's avatar plus a single muted line,
// "{author} {sentence} · {time}". The author name and the emphasized values
// (<Val>) render dark against the muted connective text, so the line reads at a
// glance. The avatar matches comment updates (see ActivityEntry) so authors line
// up down the log.
const ActivityEventEntry = ({
  entry,
  isFirstCreate = true,
}: {
  entry: CollatedActivity;
  isFirstCreate?: boolean;
}) => (
  <div className="px-4 py-3.5 border-b border-t-0 border-x-0 border-solid border-[#F4F5F8]">
    <div className="flex gap-[11px] items-center">
      <Avatar
        initials={entry.author.initials}
        color={entry.author.color}
        photoUrl={entry.author.photoUrl}
      />
      <div className="flex-1 min-w-0 text-sm text-[#676879] leading-[1.5]">
        <Val>{entry.author.name}</Val> {activitySentence(entry, isFirstCreate)}
        <span className="text-[#9699A6]"> · </span>
        <Tooltip content={absoluteTime(entry.createdAt)}>
          <span className="text-[#9699A6]">{relativeTime(entry.createdAt)}</span>
        </Tooltip>
      </div>
    </div>
  </div>
);

export default ActivityEventEntry;
